import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useAtom } from 'jotai';
import { currentUserAtom } from '../../auth/AuthAtoms';
import { getCardImageSrc } from '../../hooks/useCardImage';
import {
    fetchBinder,
    addCardToBinder,
    reorderBinderCards,
    removeCardFromBinder,
} from '../../api/binders';
import Card from '../../components/Card';
import { toast } from 'react-toastify';
import { fetchCardsByUserID } from '../../api/cards';
import LoadingSpinner from '../../components/LoadingSpinner';

const SLOTS_PER_PAGE = 9;

const BinderPage = () => {
    const { username, binderNumber } = useParams();
    const [currentUser] = useAtom(currentUserAtom);
    const [binder, setBinder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    const hasFetchedCardsRef = useRef(false);
    const [isOwner, setIsOwner] = useState(false);
    const [userCards, setUserCards] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [addingSlot, setAddingSlot] = useState(null);
    const [modalFilter, setModalFilter] = useState('');
    const [modalVariationFilter, setModalVariationFilter] = useState('all');
    const [selectedCard, setSelectedCard] = useState(null);
    const [selectedInventoryID, setSelectedInventoryID] = useState(null);
    const [selectedVariation, setSelectedVariation] = useState(null);
    const [selectedDisplayKey, setSelectedDisplayKey] = useState('');

    const loadBinder = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchBinder(username, binderNumber);
            setBinder(data);
        } catch {
            toast.error('Failed to load binder');
        } finally {
            setLoading(false);
        }
    }, [username, binderNumber]);

    useEffect(() => { loadBinder(); }, [loadBinder]);

    useEffect(() => {
        if (!currentUser) return;
        const ownerCheck = currentUser.username === username;
        setIsOwner(ownerCheck);
        if (ownerCheck && !hasFetchedCardsRef.current) {
            hasFetchedCardsRef.current = true;
            fetchCardsByUserID()
                .then(data => setUserCards(data.cards || []))
                .catch(() => toast.error('Failed to load your cards'));
        }
    }, [currentUser, username]);

    const getPageSlots = useCallback(() => {
        if (!binder) return Array(SLOTS_PER_PAGE).fill(null);
        const startSlot = (currentPage - 1) * SLOTS_PER_PAGE + 1;
        const endSlot = currentPage * SLOTS_PER_PAGE;
        const slots = Array(SLOTS_PER_PAGE).fill(null);
        binder.cards.forEach(bc => {
            if (bc.slotPosition >= startSlot && bc.slotPosition <= endSlot) {
                slots[bc.slotPosition - startSlot] = bc;
            }
        });
        return slots;
    }, [binder, currentPage]);

    const totalPages = useMemo(() => {
        if (!binder || binder.cards.length === 0) return 1;
        const maxSlot = binder.cards.reduce((max, c) => Math.max(max, c.slotPosition), 0);
        return Math.ceil(maxSlot / SLOTS_PER_PAGE);
    }, [binder]);

    const handleDragEnd = useCallback(async result => {
        if (!result.destination) return;
        const slots = getPageSlots();
        const fromIndex = result.source.index;
        const toIndex = result.destination.index;
        if (fromIndex === toIndex) return;

        const reordered = [...slots];
        const [moved] = reordered.splice(fromIndex, 1);
        reordered.splice(toIndex, 0, moved);

        const pageOffset = (currentPage - 1) * SLOTS_PER_PAGE;
        const updates = reordered
            .map((bc, idx) => bc ? { binderCardID: bc.binderCardID, slotPosition: pageOffset + idx + 1 } : null)
            .filter(Boolean);

        // Capture snapshot before optimistic update for safe revert
        const snapshot = binder.cards;

        // Optimistic update
        setBinder(prev => ({
            ...prev,
            cards: prev.cards.map(c => {
                const update = updates.find(u => u.binderCardID === c.binderCardID);
                return update ? { ...c, slotPosition: update.slotPosition } : c;
            }),
        }));

        try {
            await reorderBinderCards(binder.binderID, updates);
        } catch {
            toast.error('Failed to reorder');
            setBinder(prev => ({ ...prev, cards: snapshot }));
        }
    }, [binder, currentPage]);

    const handleRemove = useCallback(async bc => {
        try {
            await removeCardFromBinder(binder.binderID, bc.binderCardID);
            await loadBinder();
            toast.success('Card removed');
        } catch {
            toast.error('Failed to remove card');
        }
    }, [binder, loadBinder]);

    const openAddModal = slot => {
        setAddingSlot(slot);
        setSelectedCard(null);
        setSelectedInventoryID(null);
        setSelectedVariation(null);
        setSelectedDisplayKey('');
        setModalFilter('');
        setShowAddModal(true);
    };

    const getVariantKeys = card => {
        if (!card) return [];
        if (card.customImage) return ['custom'];
        const keys = [`${card.year}-base`];
        if (card.CardInventory?.Series) {
            card.CardInventory.Series.forEach(s => keys.push(s.seriesKey));
        }
        return keys;
    };

    const handleAddCard = async () => {
        if (!selectedCard || !selectedDisplayKey) return;
        try {
            await addCardToBinder(binder.binderID, selectedCard.cardID, addingSlot, selectedDisplayKey, selectedInventoryID);
            setShowAddModal(false);
            await loadBinder();
            toast.success('Card added');
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Failed to add card');
        }
    };

    const filteredCards = useMemo(() => {
        const binderInventoryIDs = new Set();
        if (binder) {
            binder.cards.forEach(bc => {
                if (bc.cardInventoryID) binderInventoryIDs.add(String(bc.cardInventoryID));
            });
        }

        return userCards.filter(({ Card: card, inventoryID, CardVariation: cv }) => {
            if (!card) return false;
            if (modalFilter && !card.name?.toLowerCase().includes(modalFilter.toLowerCase())) return false;
            if (modalVariationFilter === 'base' && cv) return false;
            if (modalVariationFilter === 'variation' && !cv) return false;
            return !binderInventoryIDs.has(String(inventoryID));
        });
    }, [userCards, modalFilter, modalVariationFilter, binder]);

    if (loading) return <LoadingSpinner />;
    if (!binder) return <p>Binder not found.</p>;

    const pageSlots = getPageSlots();

    const renderEmptySlot = (gridIndex) => {
        const slotPosition = (currentPage - 1) * SLOTS_PER_PAGE + gridIndex + 1;
        return (
            <div className="binder-slot binder-slot--empty" key={`empty-${slotPosition}`}>
                {isOwner && (
                    <button className="binder-add-btn" onClick={() => openAddModal(slotPosition)}>
                        + Add Card
                    </button>
                )}
            </div>
        );
    };

    const gridContent = isOwner ? (
        <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="binder-grid" direction="horizontal">
                {provided => (
                    <div className="binder-grid" ref={provided.innerRef} {...provided.droppableProps}>
                        {pageSlots.map((bc, idx) =>
                            bc ? (
                                <Draggable key={bc.binderCardID} draggableId={String(bc.binderCardID)} index={idx}>
                                    {(drag, snapshot) => (
                                        <div
                                            className={`binder-slot${snapshot.isDragging ? ' binder-slot--dragging' : ''}`}
                                            ref={drag.innerRef}
                                            {...drag.draggableProps}
                                            {...drag.dragHandleProps}
                                        >
                                            <Card card={{ ...bc.Card, CardInventory: { displayKey: bc.displayKey, upgrades: bc.CardInventory?.upgrades, CardVariation: bc.CardInventory?.CardVariation || null } }} variation={bc.CardInventory?.CardVariation || null} showLabel />
                                            <button className="binder-remove-btn" onClick={() => handleRemove(bc)}>
                                                Remove
                                            </button>
                                        </div>
                                    )}
                                </Draggable>
                            ) : renderEmptySlot(idx)
                        )}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    ) : (
        <div className="binder-grid">
            {pageSlots.map((bc, idx) =>
                bc ? (
                    <div className="binder-slot" key={bc.binderCardID}>
                        <Card card={{ ...bc.Card, CardInventory: { displayKey: bc.displayKey, upgrades: bc.CardInventory?.upgrades, CardVariation: bc.CardInventory?.CardVariation || null } }} variation={bc.CardInventory?.CardVariation || null} showLabel />
                    </div>
                ) : renderEmptySlot(idx)
            )}
        </div>
    );

    return (
        <div className="binder-page">
            <div className="binder-header">
                <h2>{binder.title}</h2>
                <p className="binder-owner">by {username}</p>
            </div>

            {gridContent}

            <div className="binder-pagination">
                <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
                    &laquo; Prev
                </button>
                <span>Page {currentPage} of {totalPages}</span>
                <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                    Next &raquo;
                </button>
                {isOwner && (
                    <button onClick={() => openAddModal(totalPages * SLOTS_PER_PAGE + 1)}>
                        + New Page
                    </button>
                )}
            </div>

            {showAddModal && (
                <div className="binder-modal-overlay" onClick={() => setShowAddModal(false)}>
                    <div className="binder-modal" onClick={e => e.stopPropagation()}>
                        <h3>Add Card to Slot {addingSlot}</h3>
                        <input
                            type="text"
                            placeholder="Search by player name..."
                            value={modalFilter}
                            onChange={e => setModalFilter(e.target.value)}
                            className="binder-modal-search"
                        />
                        <div className="binder-modal-filters">
                            {['all', 'base', 'variation'].map(f => (
                                <button
                                    key={f}
                                    className={`binder-filter-btn${modalVariationFilter === f ? ' active' : ''}`}
                                    onClick={() => setModalVariationFilter(f)}
                                >
                                    {f === 'all' ? 'All' : f === 'base' ? 'Base' : 'Variations'}
                                </button>
                            ))}
                        </div>
                        <div className="binder-modal-card-list">
                            {filteredCards.map(({ Card: card, inventoryID, CardVariation: cv }) => {
                                const selectEntry = () => {
                                    setSelectedCard(card);
                                    setSelectedInventoryID(inventoryID);
                                    setSelectedVariation(cv || null);
                                    setSelectedDisplayKey(card.customImage ? 'custom' : `${card.year}-base`);
                                };
                                return (
                                    <div
                                        key={inventoryID}
                                        role="button"
                                        tabIndex={0}
                                        className={`binder-modal-card-row${String(selectedInventoryID) === String(inventoryID) ? ' selected' : ''}`}
                                        onClick={selectEntry}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                selectEntry();
                                            }
                                        }}
                                    >
                                        <span>{card?.name}{cv && <span className="binder-modal-card-variation"> ({cv.name})</span>}</span>
                                        <span className="binder-modal-card-year">{card?.year}</span>
                                        <span className="binder-modal-card-rating">{card?.rating}</span>
                                        <span>{card?.positions}</span>
                                    </div>
                                );
                            })}
                        </div>
                        {selectedCard && (
                            <div className="binder-modal-variants">
                                <p>Choose background:</p>
                                <div className="binder-variant-picker">
                                    {getVariantKeys(selectedCard).map(key => (
                                        <div
                                            key={key}
                                            role="button"
                                            tabIndex={0}
                                            className={`variant-container${selectedDisplayKey === key ? ' active' : ''}`}
                                            onClick={() => setSelectedDisplayKey(key)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    setSelectedDisplayKey(key);
                                                }
                                            }}
                                        >
                                            <img
                                src={key === 'custom' ? selectedCard.customImage : getCardImageSrc(key)}
                                alt={key}
                                className="variant-image"
                            />
                                        </div>
                                    ))}
                                </div>
                                <div className="binder-modal-preview">
                                    <Card
                                        card={{ ...selectedCard, CardInventory: { displayKey: selectedDisplayKey, upgrades: selectedCard?.CardInventory?.upgrades, CardVariation: selectedVariation } }}
                                        variation={selectedVariation}
                                    />
                                </div>
                            </div>
                        )}
                        <div className="binder-modal-actions">
                            <button onClick={handleAddCard} disabled={!selectedCard || !selectedDisplayKey}>
                                Add to Binder
                            </button>
                            <button onClick={() => setShowAddModal(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BinderPage;
